/**
 * «Работы агроцикла и карта» — один виджет (решение владельца 08.10): слева
 * работы плана со статистикой по каждой, справа карта.
 *
 * Без выбранной работы карта показывает ВСЮ территорию агроцикла — контуры и
 * части по размещению (`/agrocycles/{key}/territory`, вид полей цикла);
 * выбор работы перекрашивает кадр в статусы её работ на полях
 * (`/season/contour-status?campaign_id=`, свёрнуто сервером по худшему).
 * Предписания карантина и поливы вне плана сюда не входят — у них свой блок.
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Map as MapIcon } from 'lucide-react';
import { Card } from '../../ui/Card';
import { Icon } from '../../ui/Icon';
import { EmptyState } from '../../ui/EmptyState';
import { MapWidget } from '../../map/MapWidget';
import { workStatusByContour, type MapLayerData } from '../../map/layers';
import type { WorkStatusValue } from '../../map/paint';
import { api, request, type Schemas } from '../../../api/client';
import { useRequest } from '../../../api/useRequest';
import { useAnalysisContext } from '../../../analysis/context';
import { defineMessages } from '../../../i18n/define';
import { useT } from '../../../i18n/hooks';
import { countOf, haUnit, pctAxis, num, windowShort, WORDS } from '../../lib/format';
import type { Crop, Obligation, Work } from '../../data/types';
import { workDone } from './cyclePlanModel';
import { groupWorks, JUST_CLOSED_DAYS, STARTING_SOON_DAYS, type WorkGroupKey } from './cycleFacts';
import './cyclePage.css';

const m = defineMessages({
  title: { id: 'cycle.page.works.title', defaultMessage: 'Работы агроцикла и карта', description: 'Заголовок виджета: работы плана агроцикла слева, карта справа' },
  aside: {
    id: 'cycle.page.works.aside',
    defaultMessage: '{works} плана · закрыто {done} · идёт {active} · впереди {ahead}',
    description: 'Сводка справа в заголовке виджета работ; {works} — «22 работы», остальное — числа строкой',
  },
  all: { id: 'cycle.page.works.all', defaultMessage: 'Вся территория агроцикла', description: 'Пункт списка работ: показать на карте всю территорию агроцикла (без выбранной работы)' },
  allNote: {
    id: 'cycle.page.works.allNote',
    defaultMessage: '{contours} · {land} земли по размещению',
    description: 'Подпись пункта «Вся территория»; {contours} — «1 997 контуров», {land} — «17 012,50 га»',
  },
  allLoading: { id: 'cycle.page.works.allLoading', defaultMessage: 'территория читается…', description: 'Подпись пункта «Вся территория», пока территория грузится' },
  listAria: { id: 'cycle.page.works.listAria', defaultMessage: 'Работы агроцикла: выбор для карты', description: 'aria-label списка работ слева от карты' },
  statusDone: { id: 'cycle.page.works.status.done', defaultMessage: 'закрыта', description: 'Состояние работы в списке: окно закрыто; строчными' },
  statusActive: { id: 'cycle.page.works.status.active', defaultMessage: 'идёт', description: 'Состояние работы в списке: окно идёт; строчными' },
  statusUpcoming: { id: 'cycle.page.works.status.upcoming', defaultMessage: 'впереди', description: 'Состояние работы в списке: окно впереди; строчными' },
  rowFig: {
    id: 'cycle.page.works.rowFig',
    defaultMessage: 'выполнено {pct} · {area}',
    description: 'Цифры строки работы: доля выполненного (без «в процессе», как KPI цикла) и объём; {pct} — «78,0 %», {area} — «8 604,47 га работ» или «312 работ на полях»',
  },
  areaOfWork: { id: 'cycle.page.works.areaOfWork', defaultMessage: '{ha} работ', description: 'Объём работы в гектарах работ; {ha} — «8 604,47 га»' },
  rowOverdue: { id: 'cycle.page.works.rowOverdue', defaultMessage: 'после срока {value}', description: 'Хвост строки работы: после срока без отчёта; {value} — «12,50 га» или «5 работ на полях»' },
  open: { id: 'cycle.page.works.open', defaultMessage: 'Карточка работы', description: 'Ссылка из выбранной работы: открыть страницу работы' },
  captionTerritory: {
    id: 'cycle.page.works.captionTerritory',
    defaultMessage: 'Территория агроцикла по размещению: контур целиком, частью или без сопоставленного хозяйства',
    description: 'Подпись под картой без выбранной работы',
  },
  captionTerritoryCrop: { id: 'cycle.page.works.captionTerritoryCrop', defaultMessage: 'Контуры агроцикла, цвет — культура размещения', description: 'Подпись под картой без выбранной работы на фикстурах' },
  captionWork: { id: 'cycle.page.works.captionWork', defaultMessage: 'Цвет — состояние работы на поле по работе «{name}»', description: 'Подпись под картой: раскраска по выбранной работе; {name} — название работы' },
  captionLoading: { id: 'cycle.page.works.captionLoading', defaultMessage: 'Статусы работы читаются…', description: 'Подпись под картой, пока статусы выбранной работы грузятся' },
  captionNoStatus: { id: 'cycle.page.works.captionNoStatus', defaultMessage: 'Статус контуров по работе «{name}» сервер не отдал', description: 'Подпись под картой: статусов нет; {name} — работа' },
  layerTitle: { id: 'cycle.page.works.layerTitle', defaultMessage: 'Территория агроцикла', description: 'Заголовок легенды слоя территории агроцикла' },
  coverWhole: { id: 'cycle.page.works.cover.whole', defaultMessage: 'контур целиком', description: 'Легенда карты территории: контур в агроцикле целиком' },
  coverPart: { id: 'cycle.page.works.cover.part', defaultMessage: 'часть контура', description: 'Легенда карты территории: в агроцикле только часть контура' },
  coverUnmatched: { id: 'cycle.page.works.cover.unmatched', defaultMessage: 'без хозяйства', description: 'Легенда карты территории: поле без сопоставленного хозяйства (ошибка размещения)' },
  coverNone: { id: 'cycle.page.works.cover.none', defaultMessage: 'вне агроцикла', description: 'Легенда карты территории: контур не в агроцикле' },
  groupActive: { id: 'cycle.page.works.group.active', defaultMessage: 'Идут сейчас', description: 'Подпись группы в списке работ агроцикла: окно работы идёт' },
  groupJustClosed: {
    id: 'cycle.page.works.group.justClosed',
    defaultMessage: 'Закрыты за {days} дней',
    description: 'Подпись группы в списке работ агроцикла: окно закрылось недавно; {days} — число дней',
  },
  groupStartingSoon: {
    id: 'cycle.page.works.group.startingSoon',
    defaultMessage: 'Начнутся в ближайшие {days} дней',
    description: 'Подпись группы в списке работ агроцикла: окно откроется скоро; {days} — число дней',
  },
  groupAhead: { id: 'cycle.page.works.group.ahead', defaultMessage: 'Предстоящие', description: 'Подпись группы в списке работ агроцикла: окно впереди' },
  groupClosed: { id: 'cycle.page.works.group.closed', defaultMessage: 'Закрыты раньше', description: 'Подпись группы в списке работ агроцикла: окно закрылось давно' },
  groupOrder: {
    id: 'cycle.page.works.group.order',
    defaultMessage: '{key, select, ahead {по началу окна} startingSoon {по началу окна} active {по концу окна} other {сначала просрочка}}',
    description: 'Малая подпись справа от названия группы работ: как упорядочены работы внутри группы',
  },
  emptyTitle: { id: 'cycle.page.works.emptyTitle', defaultMessage: 'Запущенных работ нет', description: 'Пустое состояние списка работ агроцикла' },
  emptyReason: { id: 'cycle.page.works.emptyReason', defaultMessage: 'кампании плана ещё не запускались — на карте вся территория агроцикла', description: 'Причина пустого списка работ' },
});

const STATUS_WORD = { done: m.statusDone, active: m.statusActive, upcoming: m.statusUpcoming } as const;

type Territory = Schemas['CycleTerritoryOut'];

export interface ObligationIndex {
  byWork: Map<string, Obligation[]>;
  contourIds: string[];
}

export function CycleWorksMap({
  cycleId,
  works,
  crops,
  onApi,
  index,
  hrefFor,
  asOf = null,
  planAhead,
  initialWorkId = null,
}: {
  cycleId: string;
  works: Work[];
  crops: Crop[] | undefined;
  onApi: boolean;
  /** Фикстуры: обязательства цикла по работам; на каноне — `null`. */
  index: ObligationIndex | null;
  hrefFor: (work: Work) => string;
  /** Дата среза: по ней работы делятся на группы «идут / недавно / скоро …». */
  asOf?: string | null;
  /** Работы плана без кампании (строка цикла сервера): в списке — «скоро / впереди», без цифр. */
  planAhead?: ReadonlyArray<{ id: number; label: string; stage: string; window_from: string | null; window_to: string | null }>;
  /** Работа (кампания), выбранная при открытии (ответ ИИ); без неё — вся территория. */
  initialWorkId?: string | null;
}) {
  const t = useT();
  const { params } = useAnalysisContext();
  const plan = useMemo(() => {
    const launchedRows = works.filter((w) => !w.source || w.source === 'plan');
    // Работы плана без кампании — строками без цифр: так счёт «впереди» в
    // заголовке совпадает с фактами шапки и виджетом плана.
    const pending: Work[] = (planAhead ?? []).map((p, i) => ({
      id: `plan-${p.id}`,
      cycle_id: cycleId,
      stage: p.stage,
      name: p.label,
      kind: launchedRows[0]?.kind ?? ('field' as Work['kind']),
      seq: 100_000 + i,
      window: { from: p.window_from ?? '', to: p.window_to ?? p.window_from ?? '' },
      status: 'upcoming',
      totals: { obligations: 0, area_ha: 0, reported: 0, verified: 0, late: null, silent: 0, area: null },
      note: null,
      source: 'plan',
    }));
    return [...launchedRows, ...pending];
  }, [works, planAhead, cycleId]);
  const launched = plan.filter((w) => w.status !== 'upcoming');
  const ahead = plan.filter((w) => w.status === 'upcoming');
  const [pickedId, setPickedId] = useState<string | null>(initialWorkId);
  const picked = launched.find((w) => w.id === pickedId) ?? null;

  const territory = useRequest<Territory | null>(
    () => (onApi ? request<Territory>(`/agrocycles/${encodeURIComponent(cycleId)}/territory`, params) : Promise.resolve(null)),
    [onApi, cycleId, JSON.stringify(params)],
  );
  const status = useRequest(
    () =>
      onApi && picked && Number.isInteger(Number(picked.id))
        ? api.seasonContourStatus({ ...params, campaign_id: Number(picked.id) })
        : Promise.resolve(null),
    [onApi, picked?.id, JSON.stringify(params)],
  );

  const territoryIds = useMemo(
    () => (onApi ? territory.data?.contours.map((c) => String(c.contour_id)) : index?.contourIds),
    [onApi, territory.data, index],
  );
  const territoryLayer: MapLayerData | undefined = useMemo(() => {
    if (!territory.data) return undefined;
    const byId: Record<string, string> = {};
    for (const c of territory.data.contours) byId[String(c.contour_id)] = c.cover;
    return {
      values: {
        title: t(m.layerTitle),
        byId,
        legend: [
          { key: 'whole', label: t(m.coverWhole), tone: 'ramp-3' },
          { key: 'part', label: t(m.coverPart), tone: 'ramp-1' },
          { key: 'unmatched', label: t(m.coverUnmatched), tone: 'warning' },
        ],
        noneLabel: t(m.coverNone),
      },
    };
  }, [territory.data, t]);

  const statusById: Record<string, WorkStatusValue> | undefined = useMemo(() => {
    if (!picked) return undefined;
    if (onApi) return status.data ? workStatusByContour(status.data.items) : undefined;
    if (!index) return undefined;
    const map: Record<string, WorkStatusValue> = {};
    for (const row of index.byWork.get(picked.id) ?? []) map[row.contour_id] = row.state;
    return map;
  }, [picked, onApi, status.data, index]);
  const workIds = useMemo(
    () => (picked && statusById ? Object.keys(statusById) : undefined),
    [picked, statusById],
  );

  const counts = {
    done: plan.filter((w) => w.status === 'done').length,
    active: plan.filter((w) => w.status === 'active').length,
    ahead: ahead.length,
  };

  const caption = picked
    ? statusById
      ? t(m.captionWork, { name: picked.name })
      : status.state === 'loading'
        ? t(m.captionLoading)
        : t(m.captionNoStatus, { name: picked.name })
    : onApi
      ? t(m.captionTerritory)
      : t(m.captionTerritoryCrop);

  const mapProps = picked && statusById
    ? { colorBy: 'workStatus' as const, workStatusById: statusById, filterIds: workIds, focusIds: workIds }
    : onApi && territoryLayer
      ? { colorBy: 'values' as const, data: territoryLayer, filterIds: territoryIds, focusIds: territoryIds }
      : { colorBy: 'crop' as const, filterIds: territoryIds, focusIds: territoryIds };

  const row = (work: Work, disabled: boolean) => {
    const area = work.totals.area;
    const done = workDone(work);
    const overdue =
      work.status === 'done' && area && area.overdue
        ? haUnit(area.overdue)
        : work.status === 'done' && work.totals.overdue
          ? countOf(work.totals.overdue, WORDS.obligation)
          : null;
    const on = picked?.id === work.id;
    return (
      <li key={work.id}>
        <button
          type="button"
          className={`cwm-row focus-ring ${on ? 'is-on' : ''}`}
          data-state={work.status}
          aria-pressed={on}
          disabled={disabled}
          onClick={() => setPickedId(on ? null : work.id)}
        >
          <span className="cwm-row__top">
            <strong className="cwm-row__name">{work.name}</strong>
            <span className="cwm-row__win num">{windowShort(work.window.from, work.window.to)}</span>
          </span>
          <span className="cwm-row__stage">{work.stage} · {t(STATUS_WORD[work.status])}</span>
          {!disabled ? (
            <>
              <span className="cpw-bar" aria-hidden="true"><i style={{ width: `${Math.round((done ?? 0) * 100)}%` }} /></span>
              <span className="cwm-row__fig">
                {t(m.rowFig, {
                  pct: done === null ? '—' : pctAxis(done * 100),
                  area: area ? t(m.areaOfWork, { ha: haUnit(area.total) }) : countOf(work.totals.obligations, WORDS.obligation),
                })}
                {overdue ? <span className="cwm-row__late"> · {t(m.rowOverdue, { value: overdue })}</span> : null}
              </span>
            </>
          ) : null}
        </button>
        {on ? (
          <Link className="cwm-row__open" to={hrefFor(work)}>{t(m.open)} →</Link>
        ) : null}
      </li>
    );
  };

  // Порядок по дате среза (замечание владельца 08.10): идущие, недавно
  // закрытые и скорые, предстоящие, давно закрытые — с подписями групп.
  const groups = useMemo(() => groupWorks(plan, asOf), [plan, asOf]);
  const groupLabel = (key: WorkGroupKey) =>
    key === 'active' ? t(m.groupActive)
      : key === 'justClosed' ? t(m.groupJustClosed, { days: JUST_CLOSED_DAYS })
        : key === 'startingSoon' ? t(m.groupStartingSoon, { days: STARTING_SOON_DAYS })
          : key === 'ahead' ? t(m.groupAhead) : t(m.groupClosed);
  const allNote = territory.data
    ? t(m.allNote, { contours: countOf(territory.data.contours.length, WORDS.contour), land: haUnit(territory.data.land_ha) })
    : index
      ? countOf(index.contourIds.length, WORDS.contour)
      : t(m.allLoading);

  /**
   * Список работ — панель управления карты: на странице слева от кадра, в
   * развёрнутом кадре — его боковой панелью (тот же выбор, компактнее).
   */
  const list = (compact: boolean) => (
    <div className={compact ? 'cwm-list cwm-list--compact' : 'cwm-list'}>
      <button
        type="button"
        className={`cwm-all focus-ring ${picked ? '' : 'is-on'}`}
        aria-pressed={!picked}
        onClick={() => setPickedId(null)}
      >
        <Icon icon={MapIcon} size={16} />
        <span>
          <strong>{t(m.all)}</strong>
          <span className="t-caption">{allNote}</span>
        </span>
      </button>
      {launched.length === 0 ? (
        <EmptyState compact title={t(m.emptyTitle)} reason={t(m.emptyReason)} />
      ) : null}
      <ul className="cwm-rows" aria-label={t(m.listAria)}>
        {groups.map((group) => (
          <li key={group.key} className="cwm-group" data-group={group.key}>
            <span className="cwm-group__label">
              <span>{groupLabel(group.key)} · {group.works.length}</span>
              {group.works.length > 1 ? <span>{t(m.groupOrder, { key: group.key })}</span> : null}
            </span>
            <ul className="cwm-rows">{group.works.map((w) => row(w, w.status === 'upcoming'))}</ul>
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <Card
      size="lg"
      className="cwm"
      title={t(m.title)}
      aside={t(m.aside, { works: countOf(plan.length, WORDS.work), done: num(counts.done), active: num(counts.active), ahead: num(counts.ahead) })}
    >
      <div className="cwm-split">
        {list(false)}
        <div className="cwm-map">
          <MapWidget
            {...mapProps}
            crops={crops}
            mode="frame"
            height="100%"
            legend="below"
            caption={caption}
            panel={{ title: t(m.title), summary: picked ? picked.name : t(m.all), content: list(true) }}
          />
        </div>
      </div>
    </Card>
  );
}
